import { AppId, FCX, ID, SelectSingle, usePlatformContext } from "@datassential/platform-ui-lib";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useHomeLtos } from "@apiHooks/useHomeLtos";
import HomeSectionHeader from "@pages/Home/components/HomeSectionHeader";
import InputRadio from "@components/new/InputRadio/InputRadio";
import { Grid } from "@components/new/Grid/Grid";
import LTOItem from "@pages/_shared/LTOItem/LTOItem";
import HomeSectionFooter from "@pages/Home/components/HomeSectionFooter";
import HomeSectionFooterLink from "@pages/Home/components/HomeSectionFooterLink";
import HomeSectionSubTitle from "@pages/Home/components/HomeSectionSubTitle";
import { PermissionLevelEnum } from "@models/PermissionSection";
import { toast } from "react-toastify";
import { requestTrialByFeature } from "@actions/page";
import { useDispatch } from "react-redux";
import HomeSectionLoader from "@pages/Home/components/HomeSectionLoader";
import useLocalStorageState from "@hooks/useLocalStorageState";

const HomeFoodsLtos: FCX = () => {
    const dispatch = useDispatch();
    const { userInfo: { products } } = usePlatformContext();

    const [launchesType, setLaunchesType] = useLocalStorageState<'latest' | 'top'>('home-ltos-is-top-launches', 'latest',);
    const selectedCategoryIdValueConverter = useCallback(
        (value: string | null): ID | null => {
            return value !== null ? Number(value) as ID : null;
        },
        [],
    )
    const [selectedCategoryId, setSelectedCategoryId] = useLocalStorageState<ID | null>(
        'home-ltos-tab',
        null,
        selectedCategoryIdValueConverter
    );
    const { isLoading, data } = useHomeLtos();

    const launchesRatings = useMemo(
        () => products[AppId.LaunchesAndRatings],
        [products]
    );

    const requestTrial = () => {
        toast.info('Request has been sent');
        return dispatch(requestTrialByFeature('scores'));
    };

    const categories = useMemo(
        () => data?.categories ?? [],
        [data]
    );

    const items = useMemo(
        () => {
            if (selectedCategoryId !== null) {
                if (launchesType === 'top') {
                    return data?.itemsData.data.topItems.get(selectedCategoryId) ?? [];
                } else {
                    return data?.itemsData.data.recentItems.get(selectedCategoryId) ?? [];
                }
            }

            return [];
        },
        [data, launchesType, selectedCategoryId]
    );

    const hasAccess = useMemo(
        () => {
            return data?.itemsData.permissionLevel === PermissionLevelEnum.Full;
        },
        [data]
    );

    useEffect(() => {
        if (categories.length > 0 && !selectedCategoryId) {
            setSelectedCategoryId(categories[0].id);
        }
    }, [categories, selectedCategoryId, setSelectedCategoryId]);

    if (isLoading) {
        return (
            <HomeSectionLoader/>
        );
    }

    return (
        <div className="HomeSection__content">
            <HomeSectionHeader className="no-pt">
                <div className="HomeSectionHeader__part">
                    <HomeSectionSubTitle>Launches</HomeSectionSubTitle>
                    <SelectSingle
                        className="no-rem"
                        value={selectedCategoryId}
                        setValue={setSelectedCategoryId}
                        options={categories}
                        dropdownProps={{
                            style: {
                                maxHeight: 300,
                                overflowX: 'hidden',
                            },
                            className: 'no-scroll-override',
                            place: "bottom-start",
                        }}
                        fieldProps={{
                            style: {
                                minWidth: 200,
                            },
                        }}
                        hasSearch
                    />
                    <InputRadio
                        label="Most Recent"
                        isActive={launchesType === 'latest'}
                        onChange={() => setLaunchesType('latest')}
                    />
                    <InputRadio
                        label="Top Performing (past 6 months)"
                        isActive={launchesType === 'top'}
                        onChange={() => setLaunchesType('top')}
                    />
                </div>
            </HomeSectionHeader>
            <Grid maxRowsCount={2}>
                {items.map((item, index) => ({
                    key: `${launchesType ? 'top' : 'recent'}-${item.id}-${index}`,
                    value: (
                        <LTOItem
                            data={item}
                            location="chain page"
                        />
                    ),
                }))}
            </Grid>
            <HomeSectionFooter style={{ marginTop: -20 }}>
                <HomeSectionFooterLink
                    url={launchesRatings?.url ?? ''}
                    text="More Launches & Ratings"
                    altText="Upgrade your subscription to reveal new items from top chains."
                    hasAccess={hasAccess}
                    onContactUsClick={requestTrial}
                    testId="linkHomeFoodsLtosMoreLaunches"
                />
            </HomeSectionFooter>
        </div>
    );
};

export default HomeFoodsLtos;